import { useState, useEffect } from 'react'
import { getPlatformAnalytics, type PlatformAnalytics } from '../../services/analytics'
import { useAuth } from '../../context/AuthContext'
import { useLanguage } from '../../context/LanguageContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import {
  ChartLine,
  TrendUp,
  TrendDown,
  Users,
  Buildings,
  Package,
  CurrencyDollar,
  Download,
  FileText,
  ShoppingCart,
  Globe,
  ArrowsClockwise,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'

export default function AnalyticsPage() {
  const { user } = useAuth()
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [timeRange, setTimeRange] = useState('30d')

  const [data, setData] = useState<PlatformAnalytics | null>(null)
  const [loadingData, setLoadingData] = useState(true)

  useEffect(() => {
    setLoadingData(true)
    getPlatformAnalytics(timeRange)
      .then(setData)
      .catch((e) => console.error('Erro ao carregar analytics', e))
      .finally(() => setLoadingData(false))
  }, [timeRange])

  const metrics = {
    totalQuotes: data?.metrics.totalQuotes ?? 0,
    totalOrders: data?.metrics.totalOrders ?? 0,
    conversionRate: data?.metrics.conversionRate ?? 0,
    averageOrderValue: data?.metrics.averageOrderValueUsd ?? 0,
    activeUsers: data?.metrics.totalUsers ?? 0,
    newUsers: data?.metrics.newUsers ?? 0,
    totalOffers: data?.metrics.totalOffers ?? 0,
    totalRevenue: data?.metrics.totalRevenueUsd ?? 0,
  }

  const series = data?.series ?? []
  const topTenants = data?.topTenants ?? []
  const MODALITY_COLORS: Record<string, string> = {
    maritimo: 'rgb(8, 145, 178)', aereo: 'rgb(139, 92, 246)', terrestre: 'rgb(5, 150, 105)',
  }
  const modalityBreakdown = (data?.modalityBreakdown ?? []).map((m) => ({
    ...m,
    label: m.name === 'maritimo' ? 'Marítimo' : m.name === 'aereo' ? 'Aéreo' : m.name === 'terrestre' ? 'Terrestre' : m.name,
    color: MODALITY_COLORS[m.name] || 'rgb(107, 114, 128)',
  }))
  const topDestinations = data?.topDestinations ?? []

  const timeRanges = [
    { value: '7d', label: '7 dias' },
    { value: '30d', label: '30 dias' },
    { value: '90d', label: '90 dias' },
    { value: '365d', label: '1 ano' },
  ]

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value)
  }

  const formatUSD = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(value)
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Analytics e Métricas
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                className="gap-2 h-9"
                onClick={() => navigate('/master/analytics/export')}
              >
                <Download size={16} />
                Exportar
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filter Bar - Horizontal Compact */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 font-medium">Período:</span>
            {timeRanges.map((range) => (
              <button
                key={range.value}
                onClick={() => setTimeRange(range.value)}
                className={`px-3 h-9 text-sm font-medium rounded-md transition-colors ${
                  timeRange === range.value
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        {/* Primary Metrics - 3 Column Grid */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Total Quotes */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Total de Cotações
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">{formatNumber(metrics.totalQuotes)}</p>
                <p className="text-sm text-gray-600 mt-1">no período</p>
              </div>
              <FileText size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>

          {/* Total Orders */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Total de Pedidos
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">{formatNumber(metrics.totalOrders)}</p>
                <p className="text-sm text-gray-600 mt-1">no período</p>
              </div>
              <ShoppingCart size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>

          {/* Conversion Rate */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Taxa de Conversão
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-emerald-600">{metrics.conversionRate}%</p>
                <p className="text-sm text-gray-600 mt-1">
                  Cotações → Pedidos
                </p>
              </div>
              <TrendUp size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>
        </div>

        {/* Secondary Metrics - 3 Column Grid */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Average Order Value */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Ticket Médio
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">{formatUSD(metrics.averageOrderValue)}</p>
                <p className="text-sm text-gray-600 mt-1">Por pedido</p>
              </div>
              <CurrencyDollar size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>

          {/* Users Activity */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Usuários
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Ativos</span>
                <span className="font-semibold text-gray-900">{formatNumber(metrics.activeUsers)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Novos este mês</span>
                <span className="font-semibold text-emerald-600">+{formatNumber(metrics.newUsers)}</span>
              </div>
            </div>
          </div>

          {/* Revenue & Growth */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Receita Total
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-emerald-600">{formatUSD(metrics.totalRevenue)}</p>
                <p className="text-sm text-gray-600 mt-1">fretes fechados (USD)</p>
              </div>
              <TrendUp size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Quote & Order Trends */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">
              Cotações e Pedidos (últimos 6 meses)
            </h3>
            {series.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-sm text-gray-400">Sem dados</div>
            ) : (
              <>
                <div className="h-56 flex items-end justify-between gap-3">
                  {series.map((s, i) => {
                    const max = Math.max(1, ...series.map((x) => x.quotes))
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center justify-end h-full gap-1">
                        <div className="w-full flex items-end justify-center gap-1 h-full">
                          <div className="w-1/2 rounded-t bg-emerald-500" style={{ height: `${(s.quotes / max) * 100}%` }} title={`${s.quotes} cotações`} />
                          <div className="w-1/2 rounded-t bg-sky-400" style={{ height: `${(s.orders / max) * 100}%` }} title={`${s.orders} pedidos`} />
                        </div>
                        <span className="text-[10px] text-gray-500">{s.label}</span>
                      </div>
                    )
                  })}
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Cotações</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-sky-400" /> Pedidos</span>
                </div>
              </>
            )}
          </div>

          {/* Revenue Trends */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">
              Resumo do Período
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Propostas enviadas</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{formatNumber(metrics.totalOffers)}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Novos usuários</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">+{formatNumber(metrics.newUsers)}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Ticket médio</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{formatUSD(metrics.averageOrderValue)}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Conversão</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">{metrics.conversionRate}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Three Column Section */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Top Tenants */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
              Top Tenants
            </h3>
            <div className="space-y-4">
              {topTenants.map((tenant, index) => (
                <div
                  key={index}
                  className="pb-4 border-b border-gray-100 last:border-0 last:pb-0"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-emerald-700">
                        {index + 1}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {tenant.name}
                    </p>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>{tenant.quotes} cotações · {tenant.orders} pedidos</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Modality Breakdown */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
              Modalidades
            </h3>
            <div className="space-y-4">
              {modalityBreakdown.map((modality, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: modality.color }}
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {modality.label}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {modality.percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        width: `${modality.percentage}%`,
                        backgroundColor: modality.color,
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatNumber(modality.count)} cotações
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Top Destinations */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
              Principais Destinos
            </h3>
            <div className="space-y-3">
              {topDestinations.map((dest, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Globe size={20} className="text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">
                      {dest.country}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {dest.quotes}
                    </p>
                    <p className="text-xs text-gray-500">cotações</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* System Performance */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Performance do Sistema
            </h3>
            <Button variant="outline" size="sm" className="gap-2 h-9">
              <ArrowsClockwise size={16} />
              Atualizar
            </Button>
          </div>
          <div className="grid grid-cols-4 gap-6">
            <div className="text-center p-4 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-500 mb-2">Tempo de Resposta</p>
              <p className="text-2xl font-bold text-gray-900">245ms</p>
              <div className="flex items-center justify-center gap-1 mt-2">
                <TrendDown size={16} className="text-emerald-600" />
                <span className="text-xs text-emerald-600 font-medium">-12%</span>
              </div>
            </div>
            <div className="text-center p-4 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-500 mb-2">Uptime</p>
              <p className="text-2xl font-bold text-gray-900">99.98%</p>
              <p className="text-xs text-gray-500 mt-2">Últimos 30 dias</p>
            </div>
            <div className="text-center p-4 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-500 mb-2">Requisições/min</p>
              <p className="text-2xl font-bold text-gray-900">1,847</p>
              <div className="flex items-center justify-center gap-1 mt-2">
                <TrendUp size={16} className="text-blue-600" />
                <span className="text-xs text-blue-600 font-medium">+8%</span>
              </div>
            </div>
            <div className="text-center p-4 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-500 mb-2">Taxa de Erro</p>
              <p className="text-2xl font-bold text-gray-900">0.12%</p>
              <div className="flex items-center justify-center gap-1 mt-2">
                <TrendDown size={16} className="text-emerald-600" />
                <span className="text-xs text-emerald-600 font-medium">-45%</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </AppLayout>
  )
}
